/**
 * Foro del Proyecto — preguntas y respuestas anidadas (hilo tipo Reddit).
 * Solo texto. Cualquier usuario pregunta/responde. Anidacion infinita.
 * Editar/eliminar solo el autor. Soft-delete conserva el hilo.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { useAuth } from '../../../context/AuthContext';
import client from '../../../services/graphqlClient';
import './ForoProyecto.css';

const QUERY = `query($pid: Int!) {
  foroProyecto(proyecto_id: $pid) {
    id parent_id usuario_id usuario_nombre contenido editado eliminado created_at es_mio
    hijos {
      id parent_id usuario_id usuario_nombre contenido editado eliminado created_at es_mio
      hijos {
        id parent_id usuario_id usuario_nombre contenido editado eliminado created_at es_mio
        hijos {
          id parent_id usuario_id usuario_nombre contenido editado eliminado created_at es_mio
          hijos {
            id parent_id usuario_id usuario_nombre contenido editado eliminado created_at es_mio
            hijos {
              id parent_id usuario_id usuario_nombre contenido editado eliminado created_at es_mio
              hijos {
                id parent_id usuario_id usuario_nombre contenido editado eliminado created_at es_mio
              }
            }
          }
        }
      }
    }
  }
}`;

const fechaRelativa = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'hace un momento';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `hace ${Math.floor(diff / 86400)} d`;
  return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ForoProyecto = ({ proyectoId }) => {
  const { success, error: showError } = useNotification();
  const { user } = useAuth();
  const [mensajes, setMensajes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nuevaPregunta, setNuevaPregunta] = useState('');
  const [enviando, setEnviando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const data = await client.query(QUERY, { pid: proyectoId });
      setMensajes(data.foroProyecto || []);
    } catch (e) {
      showError('Error cargando foro: ' + e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [proyectoId]);

  useEffect(() => {
    cargar();
    // Marcar el foro como visto al entrar (resetea el badge)
    client.mutate(`mutation($pid: Int!) { marcarForoVisto(proyecto_id: $pid) { success } }`, { pid: proyectoId }).catch(() => {});
    // eslint-disable-next-line
  }, [proyectoId]);

  const publicarPregunta = async () => {
    if (!nuevaPregunta.trim()) return;
    setEnviando(true);
    try {
      await client.mutate(
        `mutation($pid: Int!, $c: String!) { crearMensajeForo(proyecto_id: $pid, contenido: $c) { id } }`,
        { pid: proyectoId, c: nuevaPregunta.trim() }
      );
      setNuevaPregunta('');
      success('Pregunta publicada');
      cargar();
    } catch (e) {
      showError('Error: ' + e.message);
    } finally {
      setEnviando(false);
    }
  };

  const responder = async (parentId, contenido) => {
    await client.mutate(
      `mutation($pid: Int!, $c: String!, $parent: Int) { crearMensajeForo(proyecto_id: $pid, contenido: $c, parent_id: $parent) { id } }`,
      { pid: proyectoId, c: contenido, parent: parentId }
    );
    cargar();
  };

  const editar = async (id, contenido) => {
    const r = await client.mutate(
      `mutation($id: Int!, $c: String!) { editarMensajeForo(id: $id, contenido: $c) { success message } }`,
      { id, c: contenido }
    );
    if (!r.editarMensajeForo.success) throw new Error(r.editarMensajeForo.message);
    cargar();
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este mensaje?')) return;
    const r = await client.mutate(
      `mutation($id: Int!) { eliminarMensajeForo(id: $id) { success message } }`,
      { id }
    );
    if (r.eliminarMensajeForo.success) { success('Mensaje eliminado'); cargar(); }
    else showError(r.eliminarMensajeForo.message);
  };

  if (loading) return <div className="foro-loading">Cargando foro…</div>;

  return (
    <div className="foro-root">
      <div className="foro-header">
        <h2>💬 Foro del Proyecto</h2>
        <p className="foro-sub">Haz preguntas y participa en las conversaciones sobre este proyecto.</p>
      </div>

      {/* Nueva pregunta */}
      <div className="foro-nueva-pregunta">
        <textarea
          value={nuevaPregunta}
          onChange={e => setNuevaPregunta(e.target.value)}
          placeholder="Escribe una nueva pregunta o tema sobre el proyecto..."
          rows={3}
          className="foro-textarea"
        />
        <div className="foro-nueva-actions">
          <button
            className="foro-btn foro-btn-primary"
            onClick={publicarPregunta}
            disabled={enviando || !nuevaPregunta.trim()}
          >
            {enviando ? 'Publicando…' : 'Publicar pregunta'}
          </button>
        </div>
      </div>

      {/* Hilos */}
      {mensajes.length === 0 ? (
        <div className="foro-empty">
          <p>Todavía no hay preguntas en este proyecto.</p>
          <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Sé el primero en preguntar algo arriba ⬆️</p>
        </div>
      ) : (
        <div className="foro-hilos">
          {mensajes.map(m => (
            <div key={m.id} className="foro-hilo">
              <MensajeForo
                mensaje={m}
                nivel={0}
                userId={user?.id}
                onResponder={responder}
                onEditar={editar}
                onEliminar={eliminar}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Componente recursivo: renderiza un mensaje y sus hijos llamandose a si mismo
const MensajeForo = ({ mensaje, nivel, userId, onResponder, onEditar, onEliminar }) => {
  const [mostrarResponder, setMostrarResponder] = useState(false);
  const [textoRespuesta, setTextoRespuesta] = useState('');
  const [editando, setEditando] = useState(false);
  const [textoEdicion, setTextoEdicion] = useState(mensaje.contenido);
  const [enviando, setEnviando] = useState(false);
  const [colapsado, setColapsado] = useState(false);

  // Indentacion topada en nivel 6 para no agotar el ancho
  const indentNivel = Math.min(nivel, 6);
  const tieneHijos = mensaje.hijos && mensaje.hijos.length > 0;

  const enviarRespuesta = async () => {
    if (!textoRespuesta.trim()) return;
    setEnviando(true);
    try {
      await onResponder(mensaje.id, textoRespuesta.trim());
      setTextoRespuesta('');
      setMostrarResponder(false);
    } catch (e) { /* el padre maneja el error */ }
    finally { setEnviando(false); }
  };

  const guardarEdicion = async () => {
    if (!textoEdicion.trim()) return;
    setEnviando(true);
    try {
      await onEditar(mensaje.id, textoEdicion.trim());
      setEditando(false);
    } catch (e) { /* error manejado arriba */ }
    finally { setEnviando(false); }
  };

  const iniciales = (mensaje.usuario_nombre || 'U').trim().charAt(0).toUpperCase();

  return (
    <div className={`foro-msg ${nivel > 0 ? 'foro-msg-hijo' : 'foro-msg-raiz'}`} style={{ marginLeft: nivel > 0 ? `${indentNivel * 22}px` : 0 }}>
      <div className="foro-msg-card">
        <div className="foro-msg-head">
          <div className="foro-avatar">{iniciales}</div>
          <span className="foro-autor">{mensaje.usuario_nombre}</span>
          <span className="foro-fecha">{fechaRelativa(mensaje.created_at)}</span>
          {mensaje.editado && !mensaje.eliminado && <span className="foro-editado">(editado)</span>}
          {tieneHijos && (
            <button className="foro-colapsar" onClick={() => setColapsado(c => !c)} title={colapsado ? 'Expandir' : 'Colapsar'}>
              {colapsado ? `▸ ${mensaje.hijos.length} respuesta(s)` : '▾'}
            </button>
          )}
        </div>

        {editando ? (
          <div className="foro-edit-box">
            <textarea
              value={textoEdicion}
              onChange={e => setTextoEdicion(e.target.value)}
              rows={2}
              className="foro-textarea"
            />
            <div className="foro-edit-actions">
              <button className="foro-btn foro-btn-sm foro-btn-secondary" onClick={() => { setEditando(false); setTextoEdicion(mensaje.contenido); }}>Cancelar</button>
              <button className="foro-btn foro-btn-sm foro-btn-primary" onClick={guardarEdicion} disabled={enviando}>Guardar</button>
            </div>
          </div>
        ) : (
          <div className={`foro-contenido ${mensaje.eliminado ? 'foro-eliminado' : ''}`}>{mensaje.contenido}</div>
        )}

        {!editando && (
          <div className="foro-msg-acciones">
            {!mensaje.eliminado && (
              <button className="foro-accion" onClick={() => setMostrarResponder(v => !v)}>↳ Responder</button>
            )}
            {mensaje.es_mio && !mensaje.eliminado && (
              <>
                <button className="foro-accion" onClick={() => { setEditando(true); setTextoEdicion(mensaje.contenido); }}>✎ Editar</button>
                <button className="foro-accion foro-accion-del" onClick={() => onEliminar(mensaje.id)}>🗑 Eliminar</button>
              </>
            )}
          </div>
        )}

        {mostrarResponder && (
          <div className="foro-responder-box">
            <textarea
              value={textoRespuesta}
              onChange={e => setTextoRespuesta(e.target.value)}
              placeholder={`Responder a ${mensaje.usuario_nombre}...`}
              rows={2}
              className="foro-textarea"
              autoFocus
            />
            <div className="foro-edit-actions">
              <button className="foro-btn foro-btn-sm foro-btn-secondary" onClick={() => { setMostrarResponder(false); setTextoRespuesta(''); }}>Cancelar</button>
              <button className="foro-btn foro-btn-sm foro-btn-primary" onClick={enviarRespuesta} disabled={enviando || !textoRespuesta.trim()}>
                {enviando ? '…' : 'Responder'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Hijos recursivos */}
      {tieneHijos && !colapsado && (
        <div className="foro-hijos">
          {mensaje.hijos.map(h => (
            <MensajeForo
              key={h.id}
              mensaje={h}
              nivel={nivel + 1}
              userId={userId}
              onResponder={onResponder}
              onEditar={onEditar}
              onEliminar={onEliminar}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ForoProyecto;
